import { PropsWithChildren } from "react"
import {
  cn,
  NavigationMenuWrapper,
  ManagementMenu,
  ProfileDropdown,
  useCorrespondenceUnreadCountQuery,
  ManagementMenuRoutes,
  MTTEntities,
} from "toptaal-shared"
import { useCurrentUserSuspenseQuery } from "@/api/queries/useCurrentUserSuspenseQuery"
import { useTodoUnreadCountQuery } from "@/api/queries/todo/useTodoUnreadCountQuery"
import { useCan } from "@/utils/can"
import { useAuthStore } from "@/stores/useAuthStore"
import { managementItems, navigationItems } from "@/constants/navigation"
import { UserIcon } from "@heroicons/react/24/outline"
import { useLogout } from "@/hooks/mutations/useAuth"
import { TRoutes } from "@/types/routes"
import { canAccessTestSupport } from "@/utils/testSupport"

interface ScreenWrapperProps extends PropsWithChildren {
  showManagementMenu?: boolean
  className?: string
  managementMenuItemsFromProps?: ManagementMenuRoutes<TRoutes, MTTEntities>[]
}

export default function ScreenWrapper({
  children,
  showManagementMenu = false,
  className,
  managementMenuItemsFromProps,
}: ScreenWrapperProps) {
  const { logout } = useLogout()
  const { data, isLoading } = useCurrentUserSuspenseQuery()
  const user = data?.data
  const roles = useAuthStore((s) => s.user?.roles)
  const { data: unreadCount, isLoading: isCorrespondenceLoading } =
    useCorrespondenceUnreadCountQuery()
  const { data: todoUnreadCount, isLoading: isTodoLoading } =
    useTodoUnreadCountQuery()

  const managementItemsToShow = managementMenuItemsFromProps || managementItems

  const can = useCan()

  const navItems = navigationItems
    .filter((item) => {
      if (!roles || roles.length === 0) {
        return false
      }
      if (item.path === "/agenda") {
        return can("read", "calendar")
      }

      if (item.path === "/correspondentie") {
        return can("read", "correspondence")
      }

      if (item.path === "/test-support") {
        return canAccessTestSupport()
      }

      if (item.permissionEntity) {
        return can("read", item.permissionEntity)
      }

      return true
    })
    .map((item) => {
      if (item.path === "/correspondentie") {
        return { ...item, notificationCount: unreadCount }
      }
      if (item.path === "/todo") {
        return { ...item, notificationCount: todoUnreadCount }
      }

      return item
    })

  return (
    <>
      <NavigationMenuWrapper
        navigationItems={navItems}
        isTodoLoading={isTodoLoading}
        isCorrespondenceLoading={isCorrespondenceLoading}
        profileDropdown={
          <ProfileDropdown
            firstName={user.firstName}
            lastName={user.lastName}
            isLoading={isLoading}
            variant="toptaal"
            menuItems={[
              {
                label: "Profiel",
                path: "/profiel/persoonlijke_gegevens",
                icon: UserIcon,
              },
            ]}
            onLogout={logout}
          />
        }
      />
      {showManagementMenu && (
        <ManagementMenu
          managementItems={managementItemsToShow.filter((item) =>
            can("read", item.entity),
          )}
        />
      )}

      <div className={cn("screen-container", className)}>{children}</div>
    </>
  )
}
